import {
  COOKIE_CONSENT_EVENT,
  CookieCategoryName,
  canWriteCookieCategory,
  isConsentReady,
} from './categories';
import { readCookie } from './readCookie';
import { writeCookie, WriteCookieOptions } from './writeCookie';

// How long the async helpers wait for the cookie-consent script to
// dispatch a consent event before falling through to a deny-by-default
// read. Non-rejecting on timeout keeps callers free of `try/catch`.
const DEFAULT_TIMEOUT_MS = 20_000;

// How often to re-sample `isConsentReady()` while waiting. Matches the
// poll cadence `injectBannerScript` uses in `cookie-consent/index.js`.
// Covers regions where consent resolves with no `zapier.cookie-consent`
// event ever firing (auto-consent, or OneTrust removing its SDK node).
// Exported so the test can advance fake timers by the real cadence.
export const POLL_INTERVAL_MS = 250;

/** Shared by every async consent helper. */
export type WaitForConsentOptions = {
  timeoutMs?: number;
};

/**
 * Options shape `writeCookieAsync` accepts: the standard
 * `WriteCookieOptions` plus an optional `timeoutMs`. Exported so
 * consumer wrappers can type their own helpers without re-deriving
 * the intersection.
 */
export type WriteCookieAsyncOptions = WriteCookieOptions &
  WaitForConsentOptions;

/**
 * Resolves once consent state can genuinely be answered. Three signals
 * race: `isConsentReady()` being true at call time, the cookie-consent
 * script's consent event (re-checked, so a premature/placeholder event
 * can't resolve a not-ready state), and a poll that catches readiness
 * arriving with no event (auto-consent regions). The `timeoutMs` backstop
 * resolves (never rejects), as do non-browser environments, so callers can
 * fall through to a deny-by-default read without `try / catch`.
 */
export const waitForConsentReady = async ({
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: WaitForConsentOptions = {}): Promise<void> => {
  if (typeof window === 'undefined') return;
  if (isConsentReady()) return;
  await new Promise<void>((resolve) => {
    // `settle` tears down all three signals so none can re-fire. The
    // consent event and poll resolve only on genuine readiness; the
    // timeout resolves unconditionally as the backstop. `removeEventListener`,
    // `clearTimeout`, and `clearInterval` are idempotent and JavaScript's
    // single-threaded model rules out re-entry, so no `settled` flag is
    // needed. `let` is unavoidable: `settle` and the handles it clears
    // reference each other circularly.
    /* eslint-disable prefer-const */
    let timeoutHandle: ReturnType<typeof setTimeout>;
    let intervalHandle: ReturnType<typeof setInterval>;
    /* eslint-enable prefer-const */
    // A premature event (placeholder `,,`, or a dispatcher firing before
    // consent lands) must not resolve the wait. Re-check and stay attached.
    const settleIfReady = () => {
      if (isConsentReady()) settle();
    };
    const settle = () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, settleIfReady);
      clearTimeout(timeoutHandle);
      clearInterval(intervalHandle);
      resolve();
    };
    timeoutHandle = setTimeout(settle, timeoutMs);
    intervalHandle = setInterval(settleIfReady, POLL_INTERVAL_MS);
    window.addEventListener(COOKIE_CONSENT_EVENT, settleIfReady);
  });
};

/**
 * Async equivalent of `canWriteCookieCategory`. Awaits consent
 * resolution so first-page-visit callers don't get a false "no consent"
 * while OneTrust is still loading.
 *
 * The returned boolean is a point-in-time snapshot: consent can change
 * after this resolves (the user revokes via the OneTrust banner, etc.).
 * Use `writeCookie` / `writeCookieAsync` to commit cookies — both re-check
 * consent at write time. Don't cache this boolean to gate a raw
 * `document.cookie = ...` write.
 */
export const canWriteCookieCategoryAsync = async (
  category: CookieCategoryName,
  options: WaitForConsentOptions = {}
): Promise<boolean> => {
  await waitForConsentReady(options);
  return canWriteCookieCategory(category);
};

/**
 * Async equivalent of `writeCookie`. Recommended for any non-essential
 * cookie that may be written during the consent-loading window
 * (e.g. a `visitor_id` cookie set on first-page visit).
 */
export const writeCookieAsync = async (
  name: string,
  value: string,
  options: WriteCookieAsyncOptions
): Promise<boolean> => {
  const { timeoutMs, ...writeOptions } = options;
  await waitForConsentReady({ timeoutMs });
  return writeCookie(name, value, writeOptions);
};

/**
 * Async equivalent of `readCookie` for values written elsewhere on the
 * page during the consent-loading window. Pairs naturally with
 * `writeCookieAsync` for read-after-write call shapes, and lets
 * consumers emit `page_view`-style events with the resolved cookie
 * value instead of `undefined`.
 */
export const readCookieAsync = async (
  name: string,
  options: WaitForConsentOptions = {}
): Promise<string | undefined> => {
  await waitForConsentReady(options);
  return readCookie(name);
};
